import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CollectionsBookmarkRoundedIcon from '@mui/icons-material/CollectionsBookmarkRounded';
import Container from '../../components/Container';
import Main from '../../layouts/Main';
import { useAppSelector } from '../../hooks/hooks';
import { Compound } from '../../services/restserver-openapi';
import { getCompounds } from '../../utils/Host/offer';
import {
    addCompoundToCuration,
    AgentCuration,
    createCuration,
    deleteCuration,
    getCompoundLabel,
    getCurations,
    removeCompoundFromCuration,
    updateCuration,
} from '../../utils/Agent/curations';
import { compressImageFileToBase64 } from '../../utils/imageCompression';

const emptyCuration = (): AgentCuration => ({
    curationId: 0,
    curationName: '',
    description: '',
    author: '',
    visible: false,
    compoundIds: [],
    image: null,
});

const getSafeCompoundId = (compound: Compound): number => compound.compoundId ?? 0;
const getSafeCompoundName = (compound: Compound): string => compound.name ?? '';
const getSafeHostId = (compound: Compound): number => compound.hostId ?? 0;

type CompoundsTableProps = {
    compoundSearch: string;
    availableCompounds: Compound[];
    selectedCompoundIds: number[];
    onCompoundSearchChange: (value: string) => void;
    onToggleCompound: (compoundId: number) => Promise<void>;
};

const CompoundsTable = React.memo((props: CompoundsTableProps): JSX.Element => {
    const {
        compoundSearch,
        availableCompounds,
        selectedCompoundIds,
        onCompoundSearchChange,
        onToggleCompound,
    } = props;

    return (
        <Paper elevation={0} sx={{ borderRadius: 4, p: 2, boxShadow: 'none', bgcolor: 'background.paper' }}>
            <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>Compounds</Typography>
                <TextField
                    label="Nach Name, Compound-ID oder Host-ID suchen"
                    value={compoundSearch}
                    onChange={(event) => onCompoundSearchChange(event.target.value)}
                    fullWidth
                />
                <Box sx={{ maxHeight: 640, overflowY: 'auto' }}>
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: 'none', boxShadow: 'none', overflow: 'hidden', bgcolor: 'background.paper' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: 'background.paper', borderBottomColor: 'divider' } }}>
                                    {['Id', 'Name', 'HostId', 'Typ', 'Revised', 'Visible', 'Page', 'Curation'].map((header) => (
                                        <TableCell key={header}>
                                            <Typography color="text.secondary" variant="caption" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                                {header}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {availableCompounds.map((compound) => {
                                    const compoundId = getSafeCompoundId(compound);
                                    const selected = selectedCompoundIds.includes(compoundId);

                                    return (
                                        <TableRow
                                            key={compoundId}
                                            hover
                                        >
                                            <TableCell>{compoundId}</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>{getSafeCompoundName(compound)}</TableCell>
                                            <TableCell>{getSafeHostId(compound)}</TableCell>
                                            <TableCell>{compound.compoundType ?? ''}</TableCell>
                                            <TableCell align="center">
                                                {compound.revised ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                                            </TableCell>
                                            <TableCell align="center">
                                                {compound.visibleCompound ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<InfoIcon />}
                                                    sx={{ borderRadius: 2, fontWeight: 800 }}
                                                    onClick={() => window.open(`/vanlifer/Campsite/${compoundId}`, '_blank', 'noopener,noreferrer')}
                                                >
                                                    Details
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant={selected ? 'outlined' : 'contained'}
                                                    size="small"
                                                    onClick={() => {
                                                        void onToggleCompound(compoundId);
                                                    }}
                                                >
                                                    {selected ? 'Entfernen' : 'Hinzufuegen'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {availableCompounds.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8}>
                                            <Typography color="text.secondary">Keine Compounds gefunden.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Stack>
        </Paper>
    );
});

const CurationsOverviewPage = (): JSX.Element => {
    const theme = useTheme();
    const mode = theme.palette.mode;
    const isVerifiedAgent = useAppSelector((state) => state.user.isVerifiedAgent);

    const [curations, setCurations] = useState<AgentCuration[]>([]);
    const [selectedCuration, setSelectedCuration] = useState<AgentCuration>(emptyCuration());
    const [allCompounds, setAllCompounds] = useState<Compound[]>([]);
    const [compoundSearch, setCompoundSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [imagePreview, setImagePreview] = useState('');

    const compoundMap = useMemo(() => new Map(allCompounds.map((compound) => [compound.compoundId, compound])), [allCompounds]);

    const selectedCompoundIds = selectedCuration.compoundIds;
    const selectedCurationId = selectedCuration.curationId;

    const selectedCompounds = useMemo(
        () => selectedCompoundIds
            .map((compoundId) => compoundMap.get(compoundId))
            .filter((compound): compound is Compound => typeof compound !== 'undefined'),
        [compoundMap, selectedCompoundIds]
    );

    const availableCompounds = useMemo(() => {
        const normalizedSearch = compoundSearch.trim().toLowerCase();

        return allCompounds.filter((compound) => {
            const matchesSearch = !normalizedSearch
                || getSafeCompoundName(compound).toLowerCase().includes(normalizedSearch)
                || `${getSafeCompoundId(compound)}`.includes(normalizedSearch)
                || `${getSafeHostId(compound)}`.includes(normalizedSearch);

            return matchesSearch;
        });
    }, [allCompounds, compoundSearch]);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setError('');
                const [loadedCurations, loadedCompounds] = await Promise.all([
                    getCurations(),
                    getCompounds(0, 0, undefined, undefined, []),
                ]);

                setCurations(loadedCurations);
                setAllCompounds(loadedCompounds);
                const firstCuration = loadedCurations[0] ?? emptyCuration();
                setSelectedCuration(firstCuration);
                setImagePreview(firstCuration.image ? `data:image/*;base64,${firstCuration.image}` : '');
            } catch (loadError) {
                setError('Die Kuratierungen konnten nicht geladen werden.');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    const upsertCuration = useCallback((curation: AgentCuration) => {
        setCurations((prevState) => {
            const existingIndex = prevState.findIndex((entry) => entry.curationId === curation.curationId);

            if (existingIndex === -1) {
                return [curation, ...prevState];
            }

            const nextState = [...prevState];
            nextState[existingIndex] = curation;
            return nextState;
        });

        setSelectedCuration(curation);
        setImagePreview(curation.image ? `data:image/*;base64,${curation.image}` : '');
    }, []);

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        try {
            const base64Image = await compressImageFileToBase64(file, {
                maxWidth: 1600,
                maxHeight: 1200,
                maxBytes: 1_000_000,
            });
            setSelectedCuration((prevState) => ({
                ...prevState,
                image: base64Image,
            }));
            setImagePreview(URL.createObjectURL(file));
        } catch (imageError) {
            setError('Das Bild konnte nicht geladen werden.');
        }
    };

    const handleSave = async () => {
        if (!selectedCuration.curationName.trim()) {
            setError('Bitte gib einen Namen fuer die Kuratierung ein.');
            return;
        }

        try {
            setSaving(true);
            setError('');
            const savedCuration = selectedCuration.curationId === 0
                ? await createCuration(selectedCuration)
                : await updateCuration(selectedCuration);

            const curationWithLocalImage = {
                ...savedCuration,
                image: savedCuration.image ?? selectedCuration.image ?? null,
            };

            upsertCuration(curationWithLocalImage);
            setSuccessMessage('Kuratierung gespeichert.');
        } catch (saveError) {
            try {
                const refreshedCurations = await getCurations();
                setCurations(refreshedCurations);

                const reconciledCuration = selectedCuration.curationId > 0
                    ? refreshedCurations.find((curation) => curation.curationId === selectedCuration.curationId)
                    : refreshedCurations
                        .filter((curation) => curation.curationName === selectedCuration.curationName)
                        .sort((a, b) => b.curationId - a.curationId)[0];

                if (reconciledCuration) {
                    upsertCuration({
                        ...reconciledCuration,
                        image: reconciledCuration.image ?? selectedCuration.image ?? null,
                    });
                    setSuccessMessage('Kuratierung gespeichert.');
                    setError('');
                    return;
                }
            } catch {
                // fall back to original error below
            }

            setError(saveError instanceof Error ? saveError.message : 'Die Kuratierung konnte nicht gespeichert werden.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (selectedCuration.curationId === 0) {
            setSelectedCuration(emptyCuration());
            return;
        }

        try {
            setSaving(true);
            setError('');
            await deleteCuration(selectedCuration.curationId);
            const nextCurations = curations.filter((curation) => curation.curationId !== selectedCuration.curationId);
            setCurations(nextCurations);
            const nextCuration = nextCurations[0] ?? emptyCuration();
            setSelectedCuration(nextCuration);
            setImagePreview(nextCuration.image ? `data:image/*;base64,${nextCuration.image}` : '');
            setSuccessMessage('Kuratierung geloescht.');
        } catch (deleteError) {
            setError(deleteError instanceof Error ? deleteError.message : 'Die Kuratierung konnte nicht geloescht werden.');
        } finally {
            setSaving(false);
        }
    };

    const toggleCompound = useCallback(async (compoundId: number) => {
        const alreadySelected = selectedCompoundIds.includes(compoundId);

        if (selectedCurationId === 0) {
            setSelectedCuration((prevState) => ({
                ...prevState,
                compoundIds: alreadySelected
                    ? prevState.compoundIds.filter((id) => id !== compoundId)
                    : [...prevState.compoundIds, compoundId],
            }));
            return;
        }

        try {
            setSaving(true);
            setError('');
            const updatedCuration = alreadySelected
                ? await removeCompoundFromCuration(selectedCurationId, compoundId)
                : await addCompoundToCuration(selectedCurationId, compoundId);
            upsertCuration(updatedCuration);
        } catch (compoundError) {
            setError(compoundError instanceof Error ? compoundError.message : 'Die Compound-Zuordnung konnte nicht aktualisiert werden.');
        } finally {
            setSaving(false);
        }
    }, [selectedCompoundIds, selectedCurationId, upsertCuration]);

    const handleCompoundSearchChange = useCallback((value: string) => {
        setCompoundSearch(value);
    }, []);

    const ActionRow = (props: {
        icon: React.ReactNode;
        title: string;
        subtitle?: string;
        active?: boolean;
        onClick?: () => void;
        color?: 'inherit' | 'error';
    }) => {
        const { icon, title, subtitle, active, onClick, color = 'inherit' } = props;

        return (
            <ListItem disablePadding>
                <ListItemButton
                    onClick={onClick}
                    selected={active}
                    sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        gap: 0.5,
                        '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            '&:hover': { bgcolor: 'action.selected' },
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: color === 'error' ? 'error.main' : active ? 'primary.main' : 'text.secondary' }}>
                        {icon}
                    </ListItemIcon>

                    <ListItemText
                        primary={
                            <Typography sx={{ fontWeight: 800 }} noWrap>
                                {title}
                            </Typography>
                        }
                        secondary={subtitle}
                        secondaryTypographyProps={{ color: 'text.secondary' }}
                    />
                </ListItemButton>
            </ListItem>
        );
    };

    if (!isVerifiedAgent) {
        return (
            <Main>
                <Container>
                    <Paper elevation={0} sx={{ borderRadius: 4, p: 2, border: 0, boxShadow: 'none', bgcolor: 'background.paper' }}>
                        <Alert severity="error">
                            Sie haben keine Agent-Berechtigung. Womöglich ist Ihr Account noch nicht freigeschaltet.
                        </Alert>
                    </Paper>
                </Container>
            </Main>
        );
    }

    return (
        <Main>
            <Container>
                <Typography sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: 1 }} gutterBottom color="text.secondary">
                    Administration
                </Typography>

                <Box mb={3}>
                    <Typography variant="h3" component="h2" color="text.primary" fontWeight={700} lineHeight={1.2}>
                        Curations{' '}
                        <Typography
                            component="span"
                            variant="inherit"
                            color="text.primary"
                            sx={{
                                background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                    mode === 'light' ? theme.palette.secondary.main : theme.palette.primary.main,
                                    0.3
                                )} 0%)`,
                            }}
                        >
                            Overview
                        </Typography>
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Erstelle Kuratierungen und ordne Compounds flexibel mehreren Listen zu.
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
                    <Paper elevation={0} sx={{ borderRadius: 4, p: 2, boxShadow: 'none', bgcolor: 'background.paper' }}>
                        <Typography variant="caption" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 1 }} color="text.secondary">
                            Actions
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />

                        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                            <ActionRow
                                icon={<AddCircleOutlineRoundedIcon />}
                                title="Neue Kuration"
                                subtitle="Leere Kuratierung anlegen"
                                active={selectedCuration.curationId === 0}
                                onClick={() => {
                                    setSuccessMessage('');
                                    setSelectedCuration(emptyCuration());
                                    setImagePreview('');
                                }}
                            />
                            <ActionRow
                                icon={<DeleteOutlineRoundedIcon />}
                                title={selectedCuration.curationId === 0 ? 'Verwerfen' : 'Kuration löschen'}
                                subtitle={selectedCuration.curationId === 0 ? 'Neue Eingabe zurücksetzen' : 'Aktive Kuratierung entfernen'}
                                onClick={handleDelete}
                                color="error"
                            />
                        </List>

                        <Typography variant="caption" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 1 }} color="text.secondary">
                            Kuratierungen
                        </Typography>
                        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
                            {curations.map((curation) => {
                                const active = selectedCuration.curationId === curation.curationId;

                                return (
                                    <ActionRow
                                        key={curation.curationId}
                                        icon={<CollectionsBookmarkRoundedIcon />}
                                        title={curation.curationName}
                                        subtitle={`${curation.compoundIds.length} Compounds`}
                                        active={active}
                                        onClick={() => {
                                            setSuccessMessage('');
                                            upsertCuration(curation);
                                        }}
                                    />
                                );
                            })}
                            {!loading && curations.length === 0 && (
                                <Typography color="text.secondary">Noch keine Kuratierungen vorhanden.</Typography>
                            )}
                        </List>
                    </Paper>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Paper elevation={0} sx={{ borderRadius: 4, p: 2, boxShadow: 'none', bgcolor: 'background.paper' }}>
                            <Stack spacing={2}>
                                <Typography variant="h6" fontWeight={700}>Editor</Typography>
                                <TextField
                                    label="Name der Kuration"
                                    value={selectedCuration.curationName}
                                    onChange={(event) => setSelectedCuration({ ...selectedCuration, curationName: event.target.value })}
                                    fullWidth
                                />
                                <TextField
                                    label="Name des Kurators"
                                    value={selectedCuration.author}
                                    onChange={(event) => setSelectedCuration({ ...selectedCuration, author: event.target.value })}
                                    fullWidth
                                />
                                <TextField
                                    label="Beschreibung"
                                    value={selectedCuration.description}
                                    onChange={(event) => setSelectedCuration({ ...selectedCuration, description: event.target.value })}
                                    multiline
                                    minRows={5}
                                    fullWidth
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                    <Avatar
                                        src={imagePreview || undefined}
                                        variant="rounded"
                                        sx={{ width: 96, height: 96 }}
                                    />
                                    <Stack spacing={1}>
                                        <Button variant="outlined" component="label">
                                            Bild hochladen
                                            <input hidden type="file" accept="image/*" onChange={handleImageChange} />
                                        </Button>
                                        <Button
                                            variant="text"
                                            color="inherit"
                                            onClick={() => {
                                                setSelectedCuration((prevState) => ({ ...prevState, image: null }));
                                                setImagePreview('');
                                            }}
                                        >
                                            Bild entfernen
                                        </Button>
                                    </Stack>
                                </Stack>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedCuration.visible}
                                            onChange={(event) => setSelectedCuration({ ...selectedCuration, visible: event.target.checked })}
                                        />
                                    }
                                    label="Sichtbar fuer die Verwendung freischalten"
                                />
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Zugeordnete Compounds</Typography>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {selectedCompounds.map((compound) => (
                                            <Chip
                                                key={getSafeCompoundId(compound)}
                                                label={getCompoundLabel(compound)}
                                                onDelete={() => toggleCompound(getSafeCompoundId(compound))}
                                            />
                                        ))}
                                        {selectedCompounds.length === 0 && (
                                            <Typography color="text.secondary">Noch keine Compounds zugeordnet.</Typography>
                                        )}
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                                        {saving ? 'Speichern...' : 'Speichern'}
                                    </Button>
                                    <Button variant="outlined" color="error" onClick={handleDelete} disabled={saving}>
                                        {selectedCuration.curationId === 0 ? 'Verwerfen' : 'Loeschen'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        <CompoundsTable
                            compoundSearch={compoundSearch}
                            availableCompounds={availableCompounds}
                            selectedCompoundIds={selectedCompoundIds}
                            onCompoundSearchChange={handleCompoundSearchChange}
                            onToggleCompound={toggleCompound}
                        />
                    </Box>
                </Box>
            </Container>
        </Main>
    );
};

export default CurationsOverviewPage;
